import { useState, useEffect } from 'react'
import { styled } from '@mui/material/styles'
import Stack from '@mui/material/Stack'
import Paper from '@mui/material/Paper'
import Popper from '@mui/material/Popper'
import Button from '@mui/material/Button'
import Fade from '@mui/material/Fade'
import TextField from '@mui/material/TextField'
import FormGroup from '@mui/material/FormGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import Autocomplete from '@mui/material/Autocomplete'

import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'

const StyledPaper = styled(Paper)(({ theme }) => {
  return {
    position: 'relative',
    padding: '1.6rem',
    borderRadius: '0.8rem',
    background: theme.vars.palette.background.paper,
    '&::before, &::after': {
      position: 'absolute',
      top: 'calc(50% - 0.6rem)',
      left: '-0.6rem',
      border: `0.6rem solid ${theme.vars.palette.background.paper}`,
      borderTopRight: 'none',
      borderBottomRight: 'none',
      borderBottomLeftRadius: '4px',
      transform: 'rotate(37deg) skew(-15deg)',
      content: '""',
    },
    '&::after': {
      zIndex: -1,
      boxShadow: theme.shadows[1],
    },
  }
})

const getSelectValue = (filter, query) => {
  if (!filter.items) return []

  return filter.items.filter((item) => {
    if (Array.isArray(query[filter.name])) {
      return query[filter.name].includes(String(item.value))
    }

    return query[filter.name] === item.value
  })
}

function FilterPopperContent({ filter, query, setQuery }) {
  const [range, setRange] = useState({
    min: '',
    max: '',
  })
  const [selectValue, setSelectValue] = useState(getSelectValue(filter, query))

  useEffect(() => {
    if (filter.type === 'range') {
      setRange({ ...{ min: '', max: '' }, ...query[filter.name] })
    }

    if (filter.type === 'select') {
      setSelectValue(getSelectValue(filter, query))
    }
  }, [query, filter])

  const handleClick = () => {
    setQuery({
      [filter.name]: range,
    })
  }

  const handleChange = (e) => {
    const value = e.target.checked ? e.target.value : undefined
    const set = new Set(query[filter.name] ? [...query[filter.name]] : [])

    value ? set.add(value) : set.delete(e.target.value)

    setQuery({
      [filter.name]: [...set],
    })
  }

  const handleSelectChange = (items) => {
    if (!items.length) {
      setQuery({
        [filter.name]: undefined,
      })
    }

    setQuery({
      [filter.name]: items.map((it) => String(it.value)),
    })
  }

  const handleMinInputChange = (e) => {
    if (!e.target.value) {
      // setMinValue('')
      setRange({ ...range, min: '' })

      return
    }

    let value = parseInt(e.target.value, 10)

    if (value >= +filter.maxValue) value = filter.maxValue
    if (range.max !== '' && value >= +range.max) value = range.max
    if (value < +filter.minValue) value = filter.minValue

    // setMinValue(value)
    setRange({ ...range, min: value })
  }

  const handleMaxInputChange = (e) => {
    if (!e.target.value) {
      // setMaxValue('')
      setRange({ ...range, max: '' })

      return
    }

    let value = parseInt(e.target.value, 10)

    if (value > +filter.maxValue) value = filter.maxValue
    if (range.min !== '' && value <= +range.min) value = range.min
    if (value < +filter.minValue) value = filter.minValue

    // setMaxValue(value)
    setRange({ ...range, max: value })
  }

  if (!filter) return null

  if (filter.type === 'checkbox') {
    return (
      <FormGroup row>
        {filter.items.map(({ label, value, id }, index) => (
          <FormControlLabel
            key={id || label + index}
            control={<Checkbox size="small" onChange={handleChange} />}
            label={label}
            value={value}
            checked={
              (query[filter.name] &&
                query[filter.name].includes(String(value))) ||
              false
            }
          />
        ))}
      </FormGroup>
    )
  }

  if (filter.type === 'select') {
    return (
      <Autocomplete
        value={selectValue}
        multiple
        options={filter.items}
        disableCloseOnSelect
        renderTags={() => {}}
        getOptionLabel={(option) => option.label}
        onChange={(e, value) => handleSelectChange(value)}
        noOptionsText="Ничего не найдено"
        slotProps={{
          popper: {
            disablePortal: true,
          },
        }}
        renderOption={(props, option, { selected }) => {
          const { key, ...optionProps } = props
          return (
            <li key={key} {...optionProps}>
              <Checkbox
                icon={<CheckBoxOutlineBlankIcon />}
                checkedIcon={<CheckBoxIcon />}
                style={{ marginRight: 8 }}
                checked={selected}
              />
              {option.label}
            </li>
          )
        }}
        style={{ width: 300 }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={filter.label}
            placeholder={filter.label}
          />
        )}
      />
    )
  }

  if (filter.type === 'range') {
    return (
      <Stack direction="row" spacing={3.2} sx={{ alignItems: 'center' }}>
        <Stack direction="row" spacing={0.8} sx={{ alignItems: 'center' }}>
          <TextField
            type="number"
            placeholder={
              filter.minPlaceholder ||
              (filter.isPercentValue ? `${filter.minValue}%` : filter.minValue)
            }
            variant="outlined"
            size="small"
            value={range.min}
            onChange={handleMinInputChange}
            sx={{ width: '8rem' }}
          />
          <span>—</span>
          <TextField
            type="number"
            placeholder={
              filter.maxPlaceholder ||
              (filter.isPercentValue ? `${filter.maxValue}%` : filter.maxValue)
            }
            variant="outlined"
            size="small"
            value={range.max}
            onChange={handleMaxInputChange}
            sx={{ width: '8rem' }}
          />
        </Stack>
        <Button
          size="small"
          variant="contained"
          sx={{ ml: '3.2rem' }}
          onClick={handleClick}
          disabled={!(!!range.min || !!range.max)}
        >
          Применить
        </Button>
      </Stack>
    )
  }
}

const TRANSITION_DURATION_MS = 200

function FilterPopper({ anchorEl, filter, query, setQuery }) {
  const [isPopperOpen, setIsPopperOpen] = useState(false)
  const [currFilter, setCurrentFilter] = useState(null)
  const [currAnchorEl, setCurrentAnchorEl] = useState(() => undefined)

  useEffect(() => {
    if (!filter) return

    if (anchorEl) {
      setIsPopperOpen(false)
      setTimeout(() => {
        setCurrentAnchorEl(anchorEl)
      }, TRANSITION_DURATION_MS)
    }

    if (!anchorEl) {
      setIsPopperOpen(false)
      setCurrentAnchorEl(() => undefined)
    }
  }, [anchorEl])

  useEffect(() => {
    if (currAnchorEl) {
      setCurrentFilter(filter)
      setIsPopperOpen(true)
    }
  }, [currAnchorEl])

  const popperId = isPopperOpen ? `filter-popper` : undefined

  return (
    <Popper
      id={popperId}
      open={isPopperOpen}
      anchorEl={currAnchorEl}
      placement="right"
      disablePortal
      modifiers={[
        {
          name: 'offset',
          options: {
            offset: [0, 16],
          },
        },
      ]}
      transition
    >
      {({ TransitionProps }) => (
        <Fade {...TransitionProps} timeout={TRANSITION_DURATION_MS}>
          <StyledPaper>
            <FilterPopperContent
              filter={currFilter}
              query={query}
              setQuery={setQuery}
            />
          </StyledPaper>
        </Fade>
      )}
    </Popper>
  )
}

export default FilterPopper
